import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useAuth, useGet } from 'hooks';
import { Drawer } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import { searchId as id } from 'components/common';
import { getFormValues } from 'selectors/ai';
import { userHasKeywords } from 'services/api';
import SparklesIcon from 'components/common/Icon/SparklesIcon';
import { colours } from 'utils/theme';

// Import all components from our central export file
import {
  MessageTypes,
  Keywords,
  TradeshowLocation,
  FullLoader,
  Subject,
  Title,
  Message,
  ProductInfo,
  ActivitySelector,
  GenerateButtons,
  KeywordsAlert,
} from './components';

import { saveFormValues } from 'actions/ai';
import { messageTypes } from './config';

// Import utilities and hooks
import {
  processTradeShows,
  extractTradeshowsFromActivities,
} from './utils/tradeshowUtils';
import { useActivitySearch } from './hooks/useActivitySearch';
import { useMessageGeneration } from './hooks/useMessageGeneration';
import { AUTO_AI_ACTIVITIES } from './utils/scileadsAiActivities';

const GlobalStyle = createGlobalStyle`
  body {
    .ai-message-copy .ant-notification-notice-message {
      display: none;
    }
    .ai-message-copy svg {
      color: #fff;
    }
    .ai-drawer {
      z-index: 2147483600;
    }
    .ant-tooltip {
      z-index: 2147483600;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 13px;
  height: 100%;
  padding: 24px 24px 8px 24px;
  .checkbox span {
    font-size: 13px;
    color: #161616;
  }
  .button-loader .loader {
    margin: 7px 35px 0 0;
  }
`;

function AiMessage({
  researcherId,
  researcherTradeshows,
  requestTradeshows,
  city,
  requestLocation,
  workItemId,
  onClose,
  filteredTradeshows,
}) {
  const dispatch = useDispatch();
  const cacheMessage = useSelector(getFormValues);
  const { user } = useAuth();
  const [message, setMessage] = useState({
    type: user.isPartnerAccount ? 0 : messageTypes.generic,
    productName: '',
    productDescription: '',
    locationAtTradeshow: '',
    feedback: '',
    messageFeedbackId: null,
    tradeshowId: null,
    keywords: [],
    meetingTime: '',
    generateSubject: true,
    excludeOldWorkItems: workItemId ? false : true,
    activityAutoSelectCount: 0,
    ...cacheMessage,
  });

  const [tradeshows, setTradeshows] = useState(null);
  const [filteredHasActivities, setFilteredHasActivities] = useState(false);
  const [selectedWorkItemLabels, setSelectedWorkItemLabels] = useState({});
  const [
    clearActivitySelectionTrigger,
    setClearActivitySelectionTrigger,
  ] = useState(0);
  const [currentActivitiesCount, setCurrentActivitiesCount] = useState(
    message.activityAutoSelectCount
  );
  const hasSetInitialCount = useRef(false);

  // Effect to set initial count only once
  useEffect(() => {
    if (!hasSetInitialCount.current && message.activityAutoSelectCount) {
      setCurrentActivitiesCount(message.activityAutoSelectCount);
      hasSetInitialCount.current = true;
    }
  }, [message.activityAutoSelectCount]);

  // Debug effect to track count changes
  useEffect(() => {}, [currentActivitiesCount]);

  const [{ loading: keywordsLoading }] = useGet({
    url: userHasKeywords,
    onMount: true,
  });

  const getStaticActivities = useCallback(() => {
    if (
      message.type === messageTypes.tradeshow ||
      message.type === messageTypes.pastTradeshow
    ) {
      return [
        {
          value: AUTO_AI_ACTIVITIES.AI_2.value,
          key: AUTO_AI_ACTIVITIES.AI_2.value,
          label: 'Let SciLeads AI choose another relevant activity',
          title: 'Let SciLeads AI choose another relevant activity',
          displayTitle: (
            <>
              <SparklesIcon
                style={{
                  marginLeft: 0,
                  marginRight: 5,
                  padding: 0,
                  color: colours.grey,
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              />
              Let SciLeads AI choose another relevant activity
            </>
          ),
          category: 'static',
          id: AUTO_AI_ACTIVITIES.AI_2.value,
          link: null,
        },
      ];
    }

    return [
      {
        value: AUTO_AI_ACTIVITIES.AI_1.value,
        key: AUTO_AI_ACTIVITIES.AI_1.value,
        label: AUTO_AI_ACTIVITIES.AI_1.selectedLabel,
        title: AUTO_AI_ACTIVITIES.AI_1.selectedLabel,
        displayTitle: (
          <>
            <SparklesIcon
              style={{
                marginLeft: 0,
                marginRight: 5,
                padding: 0,
                color: colours.grey,
                verticalAlign: 'middle',
                display: 'inline-block',
              }}
            />
            {AUTO_AI_ACTIVITIES.AI_1.label}
          </>
        ),

        category: 'static',
        id: AUTO_AI_ACTIVITIES.AI_1.value,
        link: null,
      },
      {
        value: AUTO_AI_ACTIVITIES.AI_2.value,
        key: AUTO_AI_ACTIVITIES.AI_2.value,
        label: AUTO_AI_ACTIVITIES.AI_2.selectedLabel,
        title: AUTO_AI_ACTIVITIES.AI_2.selectedLabel,
        displayTitle: (
          <>
            <SparklesIcon
              style={{
                marginLeft: 0,
                marginRight: 5,
                padding: 0,
                color: colours.grey,
                verticalAlign: 'middle',
                display: 'inline-block',
              }}
            />
            {AUTO_AI_ACTIVITIES.AI_2.label}
          </>
        ),

        category: 'static',
        id: AUTO_AI_ACTIVITIES.AI_2.value,
        link: null,
      },
    ];
  }, [message.type]);

  // Use our custom hooks
  const {
    allItems,
    recentItems,
    searchResults,
    searchLoading,
    hasActivities,
    allItemsLoading,
    recentItemsLoading,
    handleActivitySearch,
  } = useActivitySearch(researcherId, id, message.excludeOldWorkItems);

  const {
    generatedMessages,
    generateMessageLoading,
    displayCustomKeywordsAlert,
    setDisplayCustomKeywordsAlert,
    handleSubmit,
    logMessageInteraction,
    generateButtonRef,
    generatedMessageResp,
    lastGeneratedMessageData,
  } = useMessageGeneration(message.type);

  const hasHandledInitialSelection = useRef(false);

  // Process researcher tradeshows if provided via props
  useEffect(() => {
    if (!requestTradeshows && researcherTradeshows) {
      setTradeshows(processTradeShows(researcherTradeshows));
    }
  }, [requestTradeshows, researcherTradeshows]);

  // Extract tradeshows from activity search results
  useEffect(() => {
    if (hasActivities && allItems) {
      const extractedTradeshows = extractTradeshowsFromActivities(allItems);
      if (extractedTradeshows.length > 0) {
        setTradeshows(processTradeShows(extractedTradeshows));
      }
    }
  }, [hasActivities, allItems]);

  // Track filtered activities (for tradeshow message types)
  useEffect(() => {
    setFilteredHasActivities(hasActivities);
  }, [hasActivities]);

  // Update filteredHasActivities when message type changes
  useEffect(() => {
    if (!hasActivities) {
      setFilteredHasActivities(false);
      return;
    }
    setFilteredHasActivities(hasActivities);
  }, [hasActivities, message.type]);

  const onChange = useCallback((key, value) => {
    setMessage(m => ({ ...m, [key]: value }));
  }, []);

  const onFeedbackChange = useCallback(
    v => {
      onChange('feedback', v);
    },
    [onChange]
  );

  const onClear = useCallback(() => {
    hasHandledInitialSelection.current = false;
    hasSetInitialCount.current = false;
    // Set the appropriate tradeshow ID based on the current message type
    let defaultTradeshowId = null;
    if (
      message.type === messageTypes.tradeshow &&
      tradeshows?.upcoming?.length
    ) {
      defaultTradeshowId = tradeshows.upcoming[0].id;
    } else if (
      message.type === messageTypes.pastTradeshow &&
      tradeshows?.past?.length
    ) {
      defaultTradeshowId = tradeshows.past[0].id;
    }
    setMessage(m => ({
      ...m,
      productName: '',
      productDescription: '',
      locationAtTradeshow: '',
      feedback: '',
      ...(hasActivities
        ? {
            keywords: [],
          }
        : {}),
      meetingTime: '',
      tradeshowId: defaultTradeshowId,
      messageFeedbackId: null,
      excludeOldWorkItems: true,
      activityAutoSelectCount: 0,
    }));
    setSelectedWorkItemLabels({});
    setClearActivitySelectionTrigger(t => t + 1);
  }, [tradeshows, hasActivities, message.type]);

  const handleActivitySelectionChange = useCallback(
    (items, labels) => {
      setSelectedWorkItemLabels(labels);
      let activitiesBasedOnKeywordsCount = 0;
      if (labels?.static?.[AUTO_AI_ACTIVITIES.AI_2.value]) {
        activitiesBasedOnKeywordsCount = 2;
      } else if (labels?.static?.[AUTO_AI_ACTIVITIES.AI_1.value]) {
        activitiesBasedOnKeywordsCount = 1;
      }

      // Only update if we have a valid count and it's different
      if (
        activitiesBasedOnKeywordsCount > 0 &&
        activitiesBasedOnKeywordsCount !== currentActivitiesCount
      ) {
        setCurrentActivitiesCount(activitiesBasedOnKeywordsCount);
        onChange('activityAutoSelectCount', activitiesBasedOnKeywordsCount);
      }
    },
    [onChange, setSelectedWorkItemLabels, currentActivitiesCount]
  );

  const onSubmit = useCallback(() => {
    setDisplayCustomKeywordsAlert(false);
    const hasStatic =
      selectedWorkItemLabels?.static &&
      Object.keys(selectedWorkItemLabels.static).length > 0;
    const activitiesCountToSave = hasStatic ? currentActivitiesCount : 0;
    if (!hasStatic && currentActivitiesCount !== 0) {
      setCurrentActivitiesCount(0);
    }

    onChange('activityAutoSelectCount', activitiesCountToSave);

    // Calculate the value to send to the API
    let activityAutoSelectCountForApi = activitiesCountToSave;
    if (
      (message.type === messageTypes.tradeshow ||
        message.type === messageTypes.pastTradeshow) &&
      activitiesCountToSave !== 0
    ) {
      activityAutoSelectCountForApi = 1;
    }

    dispatch(
      saveFormValues({
        productName: message.productName,
        productDescription: message.productDescription,
        keywords: message.keywords,
        excludeOldWorkItems: message.excludeOldWorkItems,
        activityAutoSelectCount: activitiesCountToSave,
      })
    );
    handleSubmit(
      { ...message, activityAutoSelectCount: activityAutoSelectCountForApi },
      selectedWorkItemLabels,
      researcherId,
      requestLocation,
      city,
      filteredTradeshows,
      hasActivities,
      recentItems
    );
  }, [
    message,
    selectedWorkItemLabels,
    researcherId,
    requestLocation,
    city,
    filteredTradeshows,
    hasActivities,
    recentItems,
    dispatch,
    handleSubmit,
    currentActivitiesCount,
    setCurrentActivitiesCount,
    onChange,
    setDisplayCustomKeywordsAlert,
  ]);

  const handleMessageTypeChange = useCallback(
    type => {
      onChange('type', type);
      if (type === messageTypes.tradeshow && tradeshows?.upcoming?.length) {
        onChange('tradeshowId', tradeshows.upcoming[0].id);
      } else if (
        type === messageTypes.pastTradeshow &&
        tradeshows?.past?.length
      ) {
        onChange('tradeshowId', tradeshows.past[0].id);
      } else {
        onChange('tradeshowId', null);
      }
    },
    [onChange, tradeshows]
  );

  const loading = keywordsLoading || generateMessageLoading;
  const tradeshowMessageType =
    message.type === messageTypes.tradeshow ||
    message.type === messageTypes.pastTradeshow;

  // Only disable if there are genuinely no activities available
  const isActivitySelectorDisabled = tradeshowMessageType
    ? !filteredHasActivities
    : !hasActivities;

  // Clear custom keywords alert when relevant state changes
  useEffect(() => {
    setDisplayCustomKeywordsAlert(false);
  }, [message.type, message.keywords, setDisplayCustomKeywordsAlert]);

  const handleCustomKeywordsAlert = () => {
    setDisplayCustomKeywordsAlert(false);
    // Clear keywords and proceed with generation without keywords
    onChange('keywords', []);
    setTimeout(() => {
      generateButtonRef.current?.click();
    }, 0);
  };

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      // Find the drawer body directly using its class
      const drawerBody = document.querySelector('.ai-drawer .ant-drawer-body');
      if (drawerBody) {
        drawerBody.scrollTop = drawerBody.scrollHeight;
      }
    }, 200); // Increase delay to ensure content is fully rendered
  }, []);

  const disabled = !message.type;
  return (
    <Drawer
      headerStyle={{ padding: '16px' }}
      bodyStyle={{ padding: 0 }}
      title={<Title />}
      footer={null}
      open={true}
      onClose={onClose}
      width={528}
      placement='right'
      className='ai-drawer'
    >
      <GlobalStyle />
      <Container onClick={e => e?.stopPropagation()}>
        <div style={{ color: '#595959' }}>
          Select one of four presets that suits your needs.
          <span style={{ color: 'red' }}>*</span>
        </div>
        <MessageTypes
          value={message.type}
          onChange={handleMessageTypeChange}
          style={{ margin: '16px 0' }}
          loading={loading}
          tradeshows={tradeshows}
          city={city}
        />
        {keywordsLoading && <FullLoader style={{ marginTop: '19px' }} />}
        {!keywordsLoading && (
          <>
            {tradeshowMessageType && (
              <TradeshowLocation
                tradeshows={
                  message.type === messageTypes.tradeshow
                    ? tradeshows?.upcoming
                    : tradeshows?.past
                }
                onTradeshowChange={value => onChange('tradeshowId', value)}
                tradeshow={message.tradeshowId}
                location={
                  message.type === messageTypes.tradeshow
                    ? message.locationAtTradeshow
                    : null
                }
                onLocationChange={value =>
                  message.type === messageTypes.tradeshow
                    ? onChange('locationAtTradeshow', value)
                    : null
                }
                loading={loading}
                style={{ marginBottom: '0px' }}
                showLocationInput={message.type === messageTypes.tradeshow}
              />
            )}

            <ProductInfo
              productName={message.productName}
              productDescription={message.productDescription}
              meetingTime={message.meetingTime}
              showMeetingTime={message.type === messageTypes.meeting}
              onProductNameChange={value => onChange('productName', value)}
              onProductDescriptionChange={value =>
                onChange('productDescription', value)
              }
              onMeetingTimeChange={value => onChange('meetingTime', value)}
              loading={loading}
              disabled={disabled}
            />

            <ActivitySelector
              allItems={allItems}
              recentItems={recentItems}
              searchResults={searchResults}
              searchLoading={searchLoading}
              hasActivities={hasActivities}
              allItemsLoading={allItemsLoading}
              recentItemsLoading={recentItemsLoading}
              workItemId={workItemId}
              excludeOldWorkItems={message.excludeOldWorkItems ?? true}
              onExcludeOldWorkItemsChange={value =>
                onChange('excludeOldWorkItems', value)
              }
              loading={loading}
              disabled={isActivitySelectorDisabled}
              onSelectionChange={handleActivitySelectionChange}
              clearSelectionTrigger={clearActivitySelectionTrigger}
              handleActivitySearch={handleActivitySearch}
              initialSelectedValues={currentActivitiesCount}
              autoAiActivities={getStaticActivities()}
              maxCount={
                message.type === messageTypes.tradeshow ||
                message.type === messageTypes.pastTradeshow
                  ? 1
                  : 2
              }
            />

            <Keywords
              value={message.keywords}
              onChange={value => onChange('keywords', value)}
              loading={loading}
              style={{ marginBottom: '16px' }}
              disabled={isActivitySelectorDisabled}
            />

            <GenerateButtons
              onClear={onClear}
              onSubmit={onSubmit}
              loading={loading}
              disabled={disabled}
              generateButtonRef={generateButtonRef}
              hasGeneratedMessage={!!generatedMessages[message.type]}
              isGenerating={generateMessageLoading}
            />

            <KeywordsAlert
              display={displayCustomKeywordsAlert}
              onProceed={handleCustomKeywordsAlert}
              onClose={() => setDisplayCustomKeywordsAlert(false)}
            />

            {message.generateSubject && (
              <Subject
                subject={generatedMessages[message.type]?.subject}
                style={{ marginBottom: '16px' }}
                loading={loading}
                shouldStream={!!generatedMessageResp}
                disabled={loading || !generatedMessages[message.type]?.subject}
              />
            )}

            <Message
              value={generatedMessages[message.type]?.message}
              shouldStream={!!generatedMessageResp}
              isError={generatedMessages[message.type]?.isError}
              disabled={loading || !generatedMessages[message.type]?.message}
              loading={generateMessageLoading}
              feedback={message.feedback}
              onFeedbackChange={onFeedbackChange}
              scrollToBottom={scrollToBottom}
              logMessageInteraction={v =>
                logMessageInteraction({
                  ...v,
                  interactionId:
                    generatedMessages[message.type]?.messageFeedbackId,
                  message: generatedMessages[message.type]?.message,
                  subject: generatedMessages[message.type]?.subject,
                  ...lastGeneratedMessageData,
                })
              }
            />
          </>
        )}
      </Container>
    </Drawer>
  );
}

AiMessage.propTypes = {
  requestLocation: PropTypes.string.isRequired,
  researcherId: PropTypes.number.isRequired,
  city: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  researcherTradeshows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
    })
  ),
  requestTradeshows: PropTypes.bool,
  filteredTradeshows: PropTypes.arrayOf(PropTypes.string),
};

export default AiMessage;
